/** Keyboard and dismissal for the small menus of the table (column menu, density, view menu).
 *
 * `usePopup` owns open/closed for a trigger-and-panel pair; this adds the menu pattern on top: the
 * arrow keys, Home and End move between the items, Escape closes (the panel's owner returns focus),
 * and Tab leaves. Items are found by role, so a menu is whatever the owner renders with
 * `role="menuitem"`, `menuitemradio` or `menuitemcheckbox`.
 */

import type { KeyboardEvent } from "react";
import { useEffect } from "react";
import type { RefObject } from "react";

const ITEM = '[role="menuitem"]:not([disabled]), [role="menuitemradio"]:not([disabled]), [role="menuitemcheckbox"]:not([disabled]), [role="option"]:not([disabled])';

export function menuItems(root: HTMLElement | null): HTMLElement[] {
  return root ? [...root.querySelectorAll<HTMLElement>(ITEM)] : [];
}

/** Arrow-key movement inside a menu. Returns true when the key was a menu key. */
export function onMenuKeyDown(event: KeyboardEvent<HTMLElement>, root: HTMLElement | null): boolean {
  const items = menuItems(root);
  if (items.length === 0) return false;
  const at = items.indexOf(document.activeElement as HTMLElement);
  const last = items.length - 1;
  const targets: Readonly<Record<string, number>> = {
    ArrowDown: at < 0 ? 0 : Math.min(last, at + 1),
    ArrowUp: at < 0 ? last : Math.max(0, at - 1),
    Home: 0,
    End: last,
  };
  const next = targets[event.key];
  if (next === undefined) return false;
  event.preventDefault();
  items[next]?.focus();
  return true;
}

/** Close on a press outside both the trigger and the panel. */
export function useDismiss(
  open: boolean,
  onClose: () => void,
  ...refs: ReadonlyArray<RefObject<HTMLElement | null>>
): void {
  useEffect(() => {
    if (!open) return;
    const onPress = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (refs.some((ref) => ref.current?.contains(target))) return;
      onClose();
    };
    document.addEventListener("pointerdown", onPress);
    return () => document.removeEventListener("pointerdown", onPress);
    // `refs` is rebuilt every render; the refs themselves are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose]);
}
